import { useEffect, useState } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import Seo from '../../components/Seo';

export default function PrivacyPolicyPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Privacy Policy · F&B Insights"
        description="How F&B Insights collects, uses, and safeguards your information."
        path="/privacy-policy"
      />
      <Navigation scrolled={scrolled} showMenuItems={false} />

      {/* Header band */}
      <header className="bg-fb-navy text-white">
        <div className="mx-auto max-w-[820px] px-8 py-14">
          <div className="mb-4 text-[11px] font-semibold uppercase tracking-[2px] text-fb-cyan">
            Legal
          </div>
          <h1 className="mb-3 text-[42px] font-bold tracking-[-1px]">Privacy Policy</h1>
          <div className="text-[15px] font-light text-white/70">Effective Date: July 2nd, 2026</div>
        </div>
      </header>

      <div className="pb-20 pt-14">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="prose prose-lg max-w-none text-gray-700">
            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Introduction</h4>
            <p className="mb-4">
              F&B Insights, LLC ("F&B Insights," "we," "us," or "our") is committed to protecting
              your privacy. This Privacy Policy explains how we collect, use, disclose, and
              safeguard your information when you use our platform and services.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Information We Collect</h4>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Information You Provide</h5>
            <p className="mb-4">
              We collect information that you provide directly to us, including:
            </p>
            <ul className="list-disc ml-8 mb-4">
              <li className="mb-2">
                Account information (name, email address, company name, phone number)
              </li>
              <li className="mb-2">Business information and data you upload to our platform</li>
              <li className="mb-2">Payment and billing information</li>
              <li className="mb-2">Communications with us</li>
              <li className="mb-2">Feedback and survey responses</li>
            </ul>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">
              Automatically Collected Information
            </h5>
            <p className="mb-4">
              When you access our services, we automatically collect certain information, including:
            </p>
            <ul className="list-disc ml-8 mb-4">
              <li className="mb-2">Log data (IP address, browser type, operating system)</li>
              <li className="mb-2">Device information</li>
              <li className="mb-2">Usage data (pages visited, features used, time spent)</li>
              <li className="mb-2">Cookies and similar tracking technologies</li>
            </ul>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">
              How We Use Your Information
            </h4>
            <p className="mb-4">We use the information we collect to:</p>
            <ul className="list-disc ml-8 mb-4">
              <li className="mb-2">Provide, maintain, and improve our services</li>
              <li className="mb-2">Process transactions and send related information</li>
              <li className="mb-2">Send technical notices, updates, and support messages</li>
              <li className="mb-2">Respond to your comments and questions</li>
              <li className="mb-2">Analyze usage patterns and trends</li>
              <li className="mb-2">
                Detect, prevent, and address technical issues and security threats
              </li>
              <li className="mb-2">Comply with legal obligations</li>
              <li className="mb-2">Send marketing communications (with your consent)</li>
            </ul>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">
              How We Share Your Information
            </h4>
            <p className="mb-4">We may share your information in the following circumstances:</p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Service Providers</h5>
            <p className="mb-4">
              We share information with third-party service providers who perform services on our
              behalf, including:
            </p>
            <ul className="list-disc ml-8 mb-4">
              <li className="mb-2">Cloud hosting providers (Amazon Web Services, Google Cloud)</li>
              <li className="mb-2">Payment processors (Stripe, Plaid)</li>
              <li className="mb-2">Analytics providers (Google Analytics)</li>
              <li className="mb-2">Customer support tools</li>
              <li className="mb-2">Security and fraud prevention services</li>
            </ul>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Business Transfers</h5>
            <p className="mb-4">
              We may share or transfer your information in connection with, or during negotiations
              of, any merger, sale of company assets, financing, or acquisition of all or a portion
              of our business.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Legal Requirements</h5>
            <p className="mb-4">
              We may disclose your information if required to do so by law or in response to valid
              requests by public authorities.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">With Your Consent</h5>
            <p className="mb-4">
              We may share your information with third parties when you give us consent to do so.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Data Security</h4>
            <p className="mb-4">
              We implement appropriate technical and organizational measures to protect your
              information against unauthorized access, alteration, disclosure, or destruction. These
              measures include:
            </p>
            <ul className="list-disc ml-8 mb-4">
              <li className="mb-2">Encryption of data in transit and at rest</li>
              <li className="mb-2">Regular security assessments</li>
              <li className="mb-2">Access controls and authentication</li>
              <li className="mb-2">Employee training on data protection</li>
              <li className="mb-2">Incident response procedures</li>
            </ul>
            <p className="mb-4">
              However, no method of transmission over the internet or electronic storage is 100%
              secure, and we cannot guarantee absolute security.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Data Retention</h4>
            <p className="mb-4">
              We retain your information for as long as necessary to provide our services and
              fulfill the purposes outlined in this Privacy Policy. When you close your account, we
              will delete or anonymize your data within six months, unless we are required to retain
              it for legal or regulatory purposes.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Your Rights and Choices</h4>
            <p className="mb-4">
              Depending on your location, you may have certain rights regarding your personal
              information:
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Access and Portability</h5>
            <p className="mb-4">
              You can request access to your personal information and receive a copy in a portable
              format.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Correction</h5>
            <p className="mb-4">
              You can update or correct your information through your account settings or by
              contacting us.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Deletion</h5>
            <p className="mb-4">
              You can request deletion of your personal information, subject to certain legal
              exceptions.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Opt-Out</h5>
            <p className="mb-4">
              You can opt out of marketing communications by following the unsubscribe instructions
              in those messages or by contacting us.
            </p>

            <h5 className="text-xl font-bold text-fb-navy mt-6 mb-4">Cookies</h5>
            <p className="mb-4">
              You can control cookies through your browser settings. Note that disabling cookies may
              affect the functionality of our services.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">
              International Data Transfers
            </h4>
            <p className="mb-4">
              Your information may be transferred to and processed in countries other than your
              country of residence. We ensure appropriate safeguards are in place for such
              transfers, including Standard Contractual Clauses approved by the European Commission.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Children's Privacy</h4>
            <p className="mb-4">
              Our services are not directed to individuals under the age of 18. We do not knowingly
              collect personal information from children. If you become aware that a child has
              provided us with personal information, please contact us.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Third-Party Links</h4>
            <p className="mb-4">
              Our services may contain links to third-party websites or services. We are not
              responsible for the privacy practices of these third parties. We encourage you to
              review their privacy policies.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">
              Changes to This Privacy Policy
            </h4>
            <p className="mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any
              material changes by posting the new Privacy Policy on this page and updating the
              "Effective Date" at the top. Your continued use of our services after such changes
              constitutes your acceptance of the updated Privacy Policy.
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">Contact Us</h4>
            <p className="mb-4">
              If you have any questions about this Privacy Policy or our privacy practices, please
              contact us at:
            </p>
            <p className="mb-2">
              <strong>{import.meta.env.VITE_COMPANY_LEGAL_NAME}</strong>
            </p>
            <p className="mb-2">{import.meta.env.VITE_COMPANY_ADDRESS_LINE1}</p>
            <p className="mb-2">{import.meta.env.VITE_COMPANY_ADDRESS_LINE2}</p>
            <p className="mb-2">
              Email:{' '}
              <a
                href={`mailto:${import.meta.env.VITE_CONTACT_EMAIL}`}
                className="text-blue-600 hover:underline"
              >
                {import.meta.env.VITE_CONTACT_EMAIL}
              </a>
            </p>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">
              Additional Information for EU/EEA Residents
            </h4>
            <p className="mb-4">
              If you are located in the European Union or European Economic Area, you have
              additional rights under the General Data Protection Regulation (GDPR):
            </p>
            <ul className="list-disc ml-8 mb-4">
              <li className="mb-2">Right to object to processing of your personal data</li>
              <li className="mb-2">Right to restrict processing of your personal data</li>
              <li className="mb-2">Right to lodge a complaint with a supervisory authority</li>
              <li className="mb-2">Right to withdraw consent at any time</li>
            </ul>
            <p className="mb-4">
              Our legal basis for processing your personal information includes:
            </p>
            <ul className="list-disc ml-8 mb-4">
              <li className="mb-2">Performance of a contract with you</li>
              <li className="mb-2">Compliance with legal obligations</li>
              <li className="mb-2">Your consent</li>
              <li className="mb-2">Our legitimate interests</li>
            </ul>

            <h4 className="text-2xl font-bold text-fb-navy mt-8 mb-4">
              Additional Information for California Residents
            </h4>
            <p className="mb-4">
              If you are a California resident, you have additional rights under the California
              Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc ml-8 mb-4">
              <li className="mb-2">
                Right to know what personal information is collected, used, shared, or sold
              </li>
              <li className="mb-2">Right to delete personal information</li>
              <li className="mb-2">
                Right to opt-out of the sale of personal information (we do not sell personal
                information)
              </li>
              <li className="mb-2">Right to non-discrimination for exercising your rights</li>
            </ul>
            <p className="mb-4">
              To exercise these rights, please contact us using the information provided above.
            </p>

            <p className="mb-6 mt-8">
              <strong>Last Updated:</strong> July 2nd, 2026
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
